import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom'
import { gql, useMutation } from '@apollo/client'
import { Stack, Box, Button, Typography, LinearProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { toast } from 'components/global/CUK/Toast/Toast'
import { trackEvent } from 'analytics'

import { useRoom } from '../../use-rooms';

const OPERATION_REMOVE = gql`
  mutation RemoveTvFromOtrum($sf_id: String!, $device_uuid: String!) {
    removeTvFromOtrum(sf_id: $sf_id, device_uuid: $device_uuid)
  }
`

const TIMER_DURATION = 10;
const WARNING_IMAGE_WIDTH = 260;
const WARNING_IMAGE_ASPECT_RATIO = '559 / 455';

interface CountdownTimerProps {
  duration: number;
  message?: string;
  onComplete: () => void;
}

export const CountdownTimer = ({ duration, onComplete }: CountdownTimerProps) => {
  const [countdown, setCountdown] = useState(duration);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (countdown === 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      onComplete();
    }
  }, [countdown, onComplete]);

  return (
    <Stack alignItems="center" justifyContent="center" gap={2} sx={{ py: 4, width: '100%' }}>
      <Typography sx={{fontSize: 13}} component="div" color="text.secondary">
        {countdown}s
      </Typography>
      <Box sx={{ width: '100%' }}>
        <LinearProgress
          variant="determinate"
          value={(countdown / duration) * 100}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>
      <Typography sx={{fontSize: 13}} color="text.secondary">
         {t('stream-cast.actions.replace.waiting')}
      </Typography>

    </Stack>
  );
};

interface StepOtrumWarningProps {
  onValidate: () => void;
}

const StepOtrumWarning = ({ onValidate }: StepOtrumWarningProps) => {
  const { t } = useTranslation()
  const room = useRoom()
  const { deviceId } = useParams();
  const [showTimer, setShowTimer] = useState(false);
  const [hasAlreadyReplaced, setHasAlreadyReplaced] = useState(false);
  const [showError, setShowError] = useState(false);

  const [replaceDevice, { loading }] = useMutation(OPERATION_REMOVE, {
    onCompleted: () => {
      trackEvent('stream_cast_otrum_removed');
      setShowTimer(true);
    },
    onError: () => {
      trackEvent('stream_cast_action_failed', { action: 'otrum_remove' });
      toast.error(t('stream-cast.actions.replace.toast_error'));
    }
  });

  const handleTimerComplete = () => {
    setShowTimer(false);
    setHasAlreadyReplaced(true);
  }

  const handleReplace = async () => {
    if (hasAlreadyReplaced) {
      setShowError(true);
      return;
    }

    await replaceDevice({
      variables: {
        sf_id: room?.site_sf_id,
        device_uuid: deviceId,
      },
    });
  }

  if (showError) {
    return (
      <Stack alignItems="center" justifyContent="center" gap={2} sx={{ py: 4 }}>
        <Typography color="error" sx={{fontSize: 13}} textAlign="center">
          {t('stream-cast.actions.replace.already_replaced_error')}
        </Typography>
      </Stack>
    );
  }

  if (showTimer) {
    return (
      <CountdownTimer
        duration={TIMER_DURATION}
        message={t('stream-cast.actions.replace.waiting')}
        onComplete={handleTimerComplete}
      />
    );
  }

  return (
    <Stack direction={'row'} gap={2}>
      <Stack gap={1} sx={{ width: WARNING_IMAGE_WIDTH }}>
        <Box
          component="img"
          src={'/images/chromecasts/tv_warning_status_error.jpg'}
          alt="TV warning status error"
          sx={{ width: '100%', aspectRatio: WARNING_IMAGE_ASPECT_RATIO, objectFit: 'contain', display: 'block' }}
        />
        <Button
          size={'small'}
          onClick={handleReplace}
          variant={'contained'}
          disabled={loading}
          sx={{ width: '100% !important' }}>
          {loading ? t('stream-cast.message.in_progress') : t('ui_elements.this_one')}
        </Button>
      </Stack>
      <Stack gap={1} sx={{ width: WARNING_IMAGE_WIDTH }}>
        <Box
          component="img"
          src={'/images/chromecasts/tv_warning_status_ok.jpg'}
          alt="TV warning status ok"
          sx={{ width: '100%', aspectRatio: WARNING_IMAGE_ASPECT_RATIO, objectFit: 'contain', display: 'block' }}
        />
        <Button size={'small'} onClick={onValidate} variant={'contained'} sx={{ width: '100% !important' }}>
          {t('ui_elements.this_one')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default StepOtrumWarning
